import React, { useState, useEffect } from 'react';
import { useOptimizedImage } from '../../hooks/useOptimizedImage';
import { imageCache } from '../../utils/imageCache';
import { Users } from './Icons';

const TeamLogo = ({
  team,
  size = 'w-10 h-10',
  textSize = 'text-lg',
  className = '',
  showBorder = false,
  borderColor = 'border-white/20',
  fallbackColors = 'from-amber-400 to-orange-500',
  rounded = 'rounded-lg',
  clickable = false,
  onClick = null
}) => {
  // Check if logo is a valid URL
  const isLogoUrl = (logo) => typeof logo === 'string' && (logo.startsWith('http://') || logo.startsWith('https://'));
  
  // Create cache key for this logo
  const logoUrl = team?.logo;
  const cacheKey = team?.id ? imageCache.getTeamLogoKey(team.id, size, logoUrl) : null;
  
  // Use optimized image loading with lazy loading disabled for logos (usually above fold)
  const {
    imageLoaded,
    imageError,
    isVisible,
    imgRef,
    containerRef,
    src,
    onLoad,
    onError
  } = useOptimizedImage(logoUrl && isLogoUrl(logoUrl) ? logoUrl : null, cacheKey, false);

  // Size mapping for Tailwind classes to pixels
  const SIZE_MAP = {
    'w-1 h-1': '4px',
    'w-1.5 h-1.5': '6px',
    'w-2 h-2': '8px',
    'w-2.5 h-2.5': '10px',
    'w-3 h-3': '12px',
    'w-3.5 h-3.5': '14px',
    'w-4 h-4': '16px',
    'w-5 h-5': '20px',
    'w-6 h-6': '24px',
    'w-7 h-7': '28px',
    'w-8 h-8': '32px',
    'w-9 h-9': '36px',
    'w-10 h-10': '40px',
    'w-11 h-11': '44px',
    'w-12 h-12': '48px',
    'w-14 h-14': '56px',
    'w-16 h-16': '64px',
    'w-18 h-18': '72px',
    'w-20 h-20': '80px',
    'w-24 h-24': '96px',
    'w-28 h-28': '112px',
    'w-32 h-32': '128px',
    'w-36 h-36': '144px',
    'w-40 h-40': '160px',
    'w-44 h-44': '176px',
    'w-48 h-48': '192px',
    'w-52 h-52': '208px',
    'w-56 h-56': '224px',
    'w-60 h-60': '240px',
    'w-64 h-64': '256px',
    'w-72 h-72': '288px',
    'w-80 h-80': '320px',
    'w-96 h-96': '384px'
  };

  // Font size mapping for Tailwind classes to pixels (optimized for emojis)
  const FONT_SIZE_MAP = {
    'text-xs': '12px',
    'text-sm': '16px',
    'text-base': '18px', 
    'text-lg': '22px',
    'text-xl': '26px',
    'text-2xl': '32px',
    'text-3xl': '40px',
    'text-4xl': '48px',
    'text-5xl': '56px',
    'text-6xl': '64px',
    'text-7xl': '72px',
    'text-8xl': '96px',
    'text-9xl': '128px'
  };

  // Extract mobile and desktop sizes from responsive classes
  const extractResponsiveSizes = (sizeClass) => {
    // Check if it's a responsive class like "w-1 h-1 sm:w-10 sm:h-10"
    const parts = sizeClass.split(' ');
    let mobileSize = null;
    let desktopSize = null;
    
    for (const part of parts) {
      if (part.startsWith('sm:')) {
        // Desktop size (sm and above)
        desktopSize = part.replace('sm:', '');
      } else if (part.startsWith('w-') && part.includes('h-')) {
        // Mobile size (no prefix)
        mobileSize = part;
      } else if (part.startsWith('w-')) {
        // Handle separate w- and h- classes
        const wClass = part;
        const hClass = parts.find(p => p.startsWith('h-') && !p.includes('sm:'));
        if (hClass) {
          mobileSize = `${wClass} ${hClass}`;
        }
      }
    }
    
    return { mobileSize, desktopSize };
  };

  const extractResponsiveTextSizes = (textClass) => {
    const parts = textClass.split(' ');
    let mobileText = null;
    let desktopText = null;
    
    for (const part of parts) {
      if (part.startsWith('sm:text-')) {
        desktopText = part.replace('sm:', '');
      } else if (part.startsWith('text-')) {
        mobileText = part;
      }
    }
    
    return { mobileText, desktopText };
  };

  // State for screen size detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { mobileSize, desktopSize } = extractResponsiveSizes(size);
  const { mobileText, desktopText } = extractResponsiveTextSizes(textSize);

  // Get pixel values based on screen size
  const currentSizeKey = isMobile ? (mobileSize || size) : (desktopSize || size);
  const currentTextKey = isMobile ? (mobileText || textSize) : (desktopText || textSize);

  const currentSizeInPx = SIZE_MAP[currentSizeKey] || '40px';
  const currentFontSizeInPx = FONT_SIZE_MAP[currentTextKey] || '16px';

  // Neutral "no image" fallback icon size (~55% of the box)
  const iconPx = Math.round((parseInt(currentSizeInPx, 10) || 40) * 0.55);

  // Safety check for team
  if (!team) {
    return (
      <div
        className={`flex items-center justify-center ${className}`}
        style={{
          width: currentSizeInPx,
          height: currentSizeInPx,
          borderRadius: rounded === 'rounded-full' ? '50%' : '8px',
          background: '#22262f',
          ...(showBorder && { border: '2px solid rgba(255,255,255,0.1)' })
        }}
      >
        <Users style={{ width: `${iconPx}px`, height: `${iconPx}px`, color: '#9ca3af' }} />
      </div>
    );
  }
  
  // Handle click events
  const handleClick = () => {
    if (onClick) {
      onClick(team);
    }
  };

  // Common click props
  const clickProps = (clickable || onClick) ? {
    onClick: handleClick,
    className: `${className} cursor-pointer hover:opacity-80 transition-opacity`,
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    }
  } : { className };

  // Common fallback styles — neutral "no image" surface
  const fallbackStyles = {
    width: currentSizeInPx,
    height: currentSizeInPx,
    borderRadius: rounded === 'rounded-full' ? '50%' : '8px',
    background: '#22262f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: currentFontSizeInPx,
    lineHeight: '1',
    ...(showBorder && {
      border: '2px solid rgba(255, 255, 255, 0.1)'
    })
  };

  // Create fallback element: keep a deliberate emoji logo if the team set one,
  // otherwise show a neutral box with a Users icon (no initials, and the
  // default 🏆 placeholder is treated as "no image").
  const FallbackLogo = ({ isLoading = false }) => {
    const trimmedLogo = typeof logoUrl === 'string' ? logoUrl.trim() : '';
    const hasEmoji = trimmedLogo !== '' && !isLogoUrl(trimmedLogo) && trimmedLogo !== '🏆';

    return (
      <div
        className={`flex items-center justify-center ${isLoading ? 'animate-pulse' : ''}`}
        style={{
          ...fallbackStyles,
          ...(hasEmoji && { background: 'transparent', fontSize: currentFontSizeInPx })
        }}
        {...clickProps}
      >
        {hasEmoji ? trimmedLogo : <Users style={{ width: `${iconPx}px`, height: `${iconPx}px`, color: '#9ca3af' }} />}
      </div>
    );
  };

  // Use the optimized loading state from the hook
  const showPlaceholder = !imageLoaded || !isVisible;

  // Show fallback if: no logo, logo is not a URL (emoji), or image failed to load
  if (!logoUrl || !isLogoUrl(logoUrl) || (!src && !logoUrl) || imageError) {
    return <FallbackLogo />;
  }

  // Show image with loading fallback
  return (
    <div ref={containerRef} className="relative overflow-hidden" style={{ width: currentSizeInPx, height: currentSizeInPx, position: 'relative' }} {...clickProps}>
      {isVisible && src && (
        <img
          ref={imgRef}
          src={src}
          alt={`${team.name || 'Team'} logo`}
          className="object-contain"
          onError={onError}
          onLoad={onLoad}
          loading="lazy" // Lazy load images
          decoding="async" // Async decode for better performance
          style={{
            width: currentSizeInPx,
            height: currentSizeInPx,
            borderRadius: rounded === 'rounded-full' ? '50%' : '8px',
            display: imageError ? 'none' : 'block',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            position: 'relative',
            zIndex: 2,
            ...(showBorder && {
              border: '2px solid rgba(255, 255, 255, 0.2)'
            })
          }}
        />
      )}
      {/* Loading placeholder - only show before image loads */}
      {showPlaceholder && !imageError && src && (
        <div
          className="absolute inset-0 animate-pulse"
          style={{
            ...fallbackStyles,
            zIndex: 1
          }}
        />
      )}
    </div>
  );
};

export default TeamLogo;